// Written by Claude: the only place the Campaigns module talks to its backend.
// Launch sends (KET-8662, GATE-684 PS-1b B) are served by web-backend (Cloud Run
// Python, europe-west1), which owns the broadcast-send job. The browser calls
// `/campaigns/v1/campaigns/launch/...` on the portal's own origin and
// functions/campaigns/[[path]].ts forwards it. web-backend verifies the Access
// JWT, the module:campaigns entitlement and the approver allowlist itself.

import type { FreshnessMeta } from "@/lib/api";

export type { FreshnessMeta };

export type Enveloped<T> = { data: T; meta: FreshnessMeta };

export type WaveState = "ready" | "running" | "sent" | "failed";

/** The latest seed or dry-run press of a wave. It never changes the wave's own `state`. */
export type TestPress = {
  kind: "seed" | "dry";
  /** A test press is reported raw: it can also be `starting` (not yet running) or `start_failed`. */
  state: WaveState | "starting" | "start_failed";
  execution: string;
  started_at: string | null;
  finished_at: string | null;
  sent: number | null;
  failed: number | null;
  by: string | null;
};

/**
 * One launch wave, exactly as GET /v1/campaigns/launch/waves returns it
 * (web-backend docs/launch-send-console.md). `state` and its execution fields
 * come from the latest LIVE press only. `audience` is null when web-backend
 * could not read D1; `sent` / `failed` are null until the job's report is read.
 */
export type Wave = {
  wave: string;
  label: string;
  /** The wave that must have sent ok first (web-backend Wave.after); absent or null = none. */
  after?: string | null;
  audience: number | null;
  by_language: Record<string, number>;
  /** Tagged for the wave but never sent by it (GATE-703 LE-2 C: non-English held). */
  held?: { count: number; by_language: Record<string, number> } | null;
  state: WaveState;
  execution: string | null;
  started_at: string | null;
  finished_at: string | null;
  sent: number | null;
  failed: number | null;
  last_test?: TestPress | null;
};

/** What a press does on the backend: the per-execution email env and dry-run flag. */
export type LaunchMode = { email_env: "dev" | "prod"; dry_run: boolean };

export type WavesResponse = { waves: Wave[]; mode: LaunchMode };

export type SendResponse = { execution: string };

export type ApiErrorBody = { error?: string; detail?: string } | null;

const BASE = import.meta.env.VITE_CAMPAIGNS_API_BASE ?? "/campaigns/v1/campaigns/launch";

const HEADERS = { Accept: "application/json", "Content-Type": "application/json" };

async function safeBody(response: Response): Promise<ApiErrorBody> {
  try {
    return (await response.json()) as ApiErrorBody;
  } catch {
    return null;
  }
}

export class CampaignsError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(`Campaigns API ${status}`);
  }
}

export async function getWaves(): Promise<Enveloped<WavesResponse>> {
  const response = await fetch(`${BASE}/waves`, { credentials: "include", headers: HEADERS, cache: "no-store" });
  if (!response.ok) throw new CampaignsError(response.status, await safeBody(response));
  return (await response.json()) as Enveloped<WavesResponse>;
}

/** POST …/waves/{wave}/send. `confirm` must equal the wave key; `seed` sends to the damien+ seed list only. */
export async function sendWave(wave: string, options: { confirm: string; seed?: boolean }): Promise<SendResponse> {
  const body: Record<string, unknown> = { confirm: options.confirm };
  if (options.seed) body.seed = true;
  const response = await fetch(`${BASE}/waves/${encodeURIComponent(wave)}/send`, {
    method: "POST",
    credentials: "include",
    headers: HEADERS,
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new CampaignsError(response.status, await safeBody(response));
  return (await response.json()) as SendResponse;
}

export const WAVES_QUERY_KEY = ["campaigns", "launch", "waves"] as const;

/** While any wave is running the tab re-reads every 10 s; otherwise it reads once. */
export const POLL_MS = 10_000;
